import { PointCloudOctree } from '@pnext/three-loader';
import { atom } from 'jotai';
import type { LngLat } from '../lib/mapSheet/types';
import type { SavedViewCamera } from '../lib/savedViews';
import type { XYZ } from '../lib/railTrace/detect';
import { DEFAULT_REVIEW, DEFAULT_TRACK_GAUGE, type ReviewSettings, type TrackGaugeSetting } from '../lib/railTrace/review';
import type { RailStation, TracePrecision } from '../lib/railTrace/trace';
import {
  autoFlySpeed,
  clampFlySpeed,
  pointCloudsDiagonal,
  readStoredFlySpeed,
  writeStoredFlySpeed,
} from '../lib/flySpeed';
import { atomFamily, atomWithStorage, createJSONStorage } from 'jotai/utils';
import * as THREE from 'three';

export type ViewerState = {
  imageId: string | null;
  googleImageId: string | null;
  activeBaseMapId: string | null;
  showGoogleStreetView: boolean;
  position: {
    x: number;
    y: number;
    z: number;
  };
};

export const DEFAULT_VIEWER_POSITION = {
  x: 0,
  y: 0,
  z: 0,
} as const;

// Type for the project's initial position (GeometryZ from GraphQL)
type GeometryZ = {
  coords: [number, number, number];
  type: string;
} | null;

const createDefaultState = (initialPosition?: { x: number; y: number; z: number }): ViewerState => ({
  imageId: null,
  googleImageId: null,
  activeBaseMapId: null,
  showGoogleStreetView: false,
  position: initialPosition ? { ...initialPosition } : { ...DEFAULT_VIEWER_POSITION },
});

// Helper function to extract position from project's initialPosition (GeometryZ)
const extractInitialPosition = (initialPosition: GeometryZ) => {
  if (!initialPosition || !initialPosition.coords) return null;
  const [x, y, z] = initialPosition.coords;
  return { x, y, z: z || 0 };
};

const storage = createJSONStorage<ViewerState>(() => sessionStorage);

// Store to keep track of project initial positions
const projectInitialPositions = new Map<string, { x: number; y: number; z: number }>();

export const setProjectInitialPosition = (projectId: string, initialPosition: GeometryZ) => {
  const extractedPosition = extractInitialPosition(initialPosition);
  if (extractedPosition) {
    projectInitialPositions.set(projectId, extractedPosition);
  }
};

// Function to update viewer state atom with initial position if currently at default
export const updateViewerStateWithInitialPosition = (
  projectId: string,
  setViewerState: (updater: (prev: ViewerState) => ViewerState) => void,
) => {
  const projectInitialPos = projectInitialPositions.get(projectId);
  if (projectInitialPos) {
    setViewerState((prev) => {
      const isDefaultPosition =
        prev.position.x === DEFAULT_VIEWER_POSITION.x &&
        prev.position.y === DEFAULT_VIEWER_POSITION.y &&
        prev.position.z === DEFAULT_VIEWER_POSITION.z;

      if (isDefaultPosition) {
        return {
          ...prev,
          position: projectInitialPos,
        };
      }
      return prev;
    });
  }
};

export const viewerStateAtomFamily = atomFamily((projectId: string) => {
  const storageKey = `@actegon.gis.viewer.${projectId}`;

  // Custom storage that checks for project initial position on first read
  const customStorage = {
    ...storage,
    getItem: (key: string, defaultValue: ViewerState) => {
      try {
        const stored = sessionStorage.getItem(key);
        if (stored) {
          const parsedData = JSON.parse(stored) as ViewerState;

          // Backward compat: fields may not exist in stored data
          if (parsedData.activeBaseMapId === undefined) {
            parsedData.activeBaseMapId = null;
          }
          if (parsedData.googleImageId === undefined) {
            parsedData.googleImageId = null;
          }
          if (parsedData.showGoogleStreetView === undefined) {
            parsedData.showGoogleStreetView = false;
          }

          // Check if stored position is default and we have a project initial position
          const projectInitialPos = projectInitialPositions.get(projectId);
          const isDefaultPosition =
            parsedData.position.x === DEFAULT_VIEWER_POSITION.x &&
            parsedData.position.y === DEFAULT_VIEWER_POSITION.y &&
            parsedData.position.z === DEFAULT_VIEWER_POSITION.z;

          if (isDefaultPosition && projectInitialPos) {
            // Update with project initial position
            const updatedData = {
              ...parsedData,
              position: projectInitialPos,
            };
            // Save the updated data back to storage
            try {
              sessionStorage.setItem(key, JSON.stringify(updatedData));
            } catch {
              // Ignore storage errors
            }
            return updatedData;
          }

          return parsedData;
        }

        // No stored data, check if we have project initial position
        const projectInitialPos = projectInitialPositions.get(projectId);
        if (projectInitialPos) {
          return createDefaultState(projectInitialPos);
        }

        return defaultValue;
      } catch {
        // Parse error, check if we have project initial position
        const projectInitialPos = projectInitialPositions.get(projectId);
        if (projectInitialPos) {
          return createDefaultState(projectInitialPos);
        }
        return defaultValue;
      }
    },
  };

  const persistedAtom = atomWithStorage<ViewerState>(storageKey, createDefaultState(), customStorage);

  return atom(
    (get) => get(persistedAtom),
    (get, set, updater: Partial<ViewerState> | ((prev: ViewerState) => ViewerState)) => {
      const previous = get(persistedAtom);
      const nextValue =
        typeof updater === 'function'
          ? (updater as (prev: ViewerState) => ViewerState)(previous)
          : {
              ...previous,
              ...updater,
              position: updater.position ?? previous.position,
            };

      set(persistedAtom, nextValue);
    },
  );
});

/**
 * Where the on-screen 2D map last came to rest (written on `moveend` and on
 * load), so the map sheet composer can start from the view the user was
 * looking at. Null until a map has loaded. Lon/lat; in a local-coordinate
 * project these are the synthetic lon/lat of `core/localTransform`.
 */
export interface MapViewSnapshot {
  centerLngLat: LngLat;
  boundsLngLat: [LngLat, LngLat];
  zoom: number;
}

export const mapViewSnapshotAtom = atomFamily((_projectId: string) => atom<MapViewSnapshot | null>(null));

/**
 * How many full-screen tools are covering the 3D viewer (the map sheet
 * composer). While > 0 the viewer's render loop is paused: nothing of it is
 * visible, and its point clouds and splats otherwise kept the GPU busy at
 * full frame rate behind the composer's own print map (Task 12).
 */
export const viewerPauseCountAtom = atom(0);

// Add Point Mode state management
export type AddPointMode = {
  enabled: boolean;
  targetLayerId: string | null;
  draftFeature: {
    geometry: { type: 'Point'; coordinates: [number, number, number] };
    properties: Record<string, any>;
  } | null;
};

export const addPointModeAtom = atom<AddPointMode>({
  enabled: false,
  targetLayerId: null,
  draftFeature: null,
});

// Add Surface Mode state management — flat reference polygon on a VECTOR layer.
// Two flavours, distinguished by `polygonMode`:
//
//  • Rectangle (default, polygonMode=false): exactly 3 picks → rectangle with full
//    edge / corner / center / normal drag handles. The original UX. Best for
//    flat walls, slabs, roofs where the user wants snap-to-rectangle.
//
//  • Outline (polygonMode=true, GIS-450): N picks (≥3), terminated by an explicit
//    Finalize button. Each click appends a vertex; Undo pops the last one.
//    Finalize fits one plane through the picks and keeps the clicked outline,
//    projected onto it (shape 'outline'). Adjust phase: corner handles, edge
//    ghost handles that insert a corner, right-click a corner to remove it.
export type AddSurfacePhase =
  | 'picking-1'
  | 'picking-2'
  | 'picking-3'
  // Polygon mode only — entered after the third pick when polygonMode is on.
  // Stays here until the user clicks Finalize on the toolbar (or Undo back to
  // 'picking-3'). Each subsequent click appends another vertex.
  | 'picking-n'
  | 'adjusting';

export type AddSurfaceMode = {
  enabled: boolean;
  targetLayerId: string | null;
  phase: AddSurfacePhase;
  pickedPoints: Array<[number, number, number]>;
  // Length is 4 for rectangles (legacy [A, B, C, D]) and N≥3 for outlines.
  draftCorners: Array<[number, number, number]> | null;
  // What draftCorners hold in the adjust phase: a rectangle (rectangle handles,
  // panel edits width / height) or a planar outline of any number of corners.
  shape: 'rect' | 'outline';
  // Set when re-editing an existing surface feature; OK calls feature.onSave instead of createFeature.
  editFeatureId: string | null;
  // When true, the AB edge (first two clicks) is forced horizontal: B's Z is snapped
  // to A's Z before building the rectangle. The plane can still tilt around AB based
  // on click 3, so sloped reference planes remain expressible.
  horizontalLock: boolean;
  // When true, the rectangle becomes a perfectly vertical wall: AB horizontal AND
  // the v-axis aligned with world up. Click 3's Z determines wall height; its XY
  // is ignored. Implies horizontalLock for AB.
  verticalLock: boolean;
  // When true, picking continues past 3 clicks until the user hits Finalize.
  // Cleared by reset(). Locked once picking starts so the user can't switch
  // mid-flow (would invalidate the partial state).
  polygonMode: boolean;
};

export const DEFAULT_ADD_SURFACE_MODE: AddSurfaceMode = {
  enabled: false,
  targetLayerId: null,
  phase: 'picking-1',
  pickedPoints: [],
  draftCorners: null,
  shape: 'rect',
  editFeatureId: null,
  horizontalLock: true,
  verticalLock: false,
  polygonMode: false,
};

export const addSurfaceModeAtom = atom<AddSurfaceMode>(DEFAULT_ADD_SURFACE_MODE);

// Add Line Mode — N-point picking that produces a LineString (1 part) or
// MultiLineString (≥2 parts). `parts` holds finalised disjoint strokes;
// `currentPart` is the in-progress stroke. Hitting "+ Part" pushes the
// current part into `parts` and resets `currentPart` to []. Finalize commits
// to the adjusting phase, where editing is delegated to GeoJson.tsx's
// VertexMover (which already supports both LineString and MultiLineString).
export type AddGeometryPhase = 'picking' | 'adjusting';
export type LinePart = Array<[number, number, number]>;

export type AddLineMode = {
  enabled: boolean;
  targetLayerId: string | null;
  phase: AddGeometryPhase;
  parts: LinePart[];
  currentPart: LinePart;
  editFeatureId: string | null;
};

export const DEFAULT_ADD_LINE_MODE: AddLineMode = {
  enabled: false,
  targetLayerId: null,
  phase: 'picking',
  parts: [],
  currentPart: [],
  editFeatureId: null,
};

export const addLineModeAtom = atom<AddLineMode>(DEFAULT_ADD_LINE_MODE);

// "Alignment from rails" (v2): one click on the track, the follower
// (`lib/railTrace/`) traces both rail heads, the panel reviews and saves.
// - picking: waiting for the click on a rail head / the track;
// - tracing: following the rails (`stations` grows live);
// - review: trim / bridge / smooth / reverse, then save. A click on the trace
//   sets the END (the trace is cut from the first click to it); a click on the
//   track beyond it continues the trace from there (joined, hole bridged).
// `returnToCloudId`: opened from the clearance dialog on that cloud — after
// saving, the dialog comes back with the new alignment picked.
export type RailTracePhase = 'picking' | 'tracing' | 'review';
/** 'stoppedEarly' (neutral, no miss marker): the user stopped the trace before any metres were traced. */
export type RailTraceMessage =
  | 'miss'
  | 'noTrack'
  | 'failed'
  | 'otherTrack'
  | 'extendFar'
  | 'stoppedEarly'
  | 'notInLine'
  | null;

export type RailTraceMode = {
  enabled: boolean;
  phase: RailTracePhase;
  targetLayerId: string | null;
  returnToCloudId: string | null;
  /** The cloud the first click hit; the follower reads only it. */
  cloudLayerId: string | null;
  click: XYZ | null;
  endClick: XYZ | null;
  stations: RailStation[];
  /** Index of the click's station in `stations`. */
  startIndex: number;
  review: ReviewSettings;
  message: RailTraceMessage;
  /** Trace chainage under the pointer in the panel's graph (the 3D marker), m. */
  hoverS: number | null;
  /** Review: the station clicked in 3D (trace chainage, m) — the card offers to cut there. */
  pickS?: number | null;
  /** Review: "Pick where it continues" armed for this end; the next click on a point in line with it continues the trace. */
  continueFrom?: 'back' | 'ahead' | null;
  /** While tracing: the follower's live report (`RailTraceLive`). Null in picking and review. */
  progress?: RailTraceLive | null;
  /**
   * The last finished trace's per-direction report (how far each way got and
   * why it ended), kept for the review card. Set when the trace enters
   * review (after a continuation: the end it extended takes the
   * continuation's far end); cleared when a new trace starts or the tool closes.
   */
  lastTrace?: {
    dirs: RailTraceLive['dirs'];
    /** A continuation was joined on: the joined end's metres are the continuation's, not the trace's. */
    joined?: boolean;
  } | null;
  /**
   * While a finished trace is being continued (phase 'tracing'): the click it
   * is continued from. The reviewed trace (`stations`, `review`) stays as it
   * was and is still drawn; null otherwise.
   */
  extending?: XYZ | null;
  /**
   * While a continuation is traced: its own stations so far (the follower's
   * `onProgress`), drawn live like a first trace's while `stations` keeps the
   * reviewed one. Null otherwise.
   */
  extendStations?: RailStation[] | null;
  /** Index of the continuation's click station in `extendStations`. */
  extendStartIndex?: number;
  /**
   * The last time a continuation added stations BEFORE the start (a back
   * "Continue ◀", or an extend joined on at the start): every chainage of the
   * old stations moved up by `m` metres. `seq` grows by one per such shift (so
   * two equal shifts are told apart). For state that lives outside the atom
   * and holds chainages (the panel's chart selection): shift it by `m` when
   * `seq` changes. Trim and bridges in `review` are already shifted.
   */
  reviewShift?: { m: number; seq: number } | null;
  /**
   * Editing a saved alignment ("Edit in rail tool", `openSavedRailAlignment`): Save updates that
   * feature (its other properties kept) instead of adding one. `stations` and `review` are what
   * was loaded: while the mode still holds those very objects nothing was changed, so closing asks
   * nothing.
   */
  editing?: {
    featureId: string;
    name: string;
    properties: Record<string, unknown>;
    stations: RailStation[];
    review: ReviewSettings;
  } | null;
};

/** One direction of the follower: walking, waiting for points, or done. */
export type RailTraceDirState = 'following' | 'loading' | 'ended';
/**
 * Why a direction ended:
 * - 'endOfData': no points any more past the last rail (the end of the scan);
 * - 'gap': 20 m without a rail where there are points (a level crossing, a switch, buried rails);
 * - 'maxLength': the length cap each way (1 km);
 * - 'user': stopped by the user (`stopRailTraceSide`), its stations kept;
 * - 'error': a point read failed, its stations kept.
 */
export type RailTraceEndReason = 'endOfData' | 'gap' | 'maxLength' | 'user' | 'error';
export type RailTraceDir = {
  /** Metres walked that way (1 m stations, trailing gap stations included while tracing). */
  metres: number;
  /** 'loading' = that direction waits for points (octree loads). */
  state: RailTraceDirState;
  /** Set once `state` is 'ended'. */
  end?: RailTraceEndReason;
  /** Metres since that way last measured both rails (one rail or none): 0 while both are found. */
  oneRailRunM: number;
};
/**
 * The follower's live report while tracing (`mode.progress`).
 * 'back' is the stations before `startIndex`, 'ahead' the ones after it.
 */
export type RailTraceLive = {
  /** 'finding' = reading around the click and finding the track, before the first station. */
  phase: 'finding' | 'tracing';
  /** `performance.now()` when the trace started. */
  startedAt: number;
  dirs: Record<'back' | 'ahead', RailTraceDir>;
  /** Debug: points read so far, octree nodes the follower loaded (and of those still in memory). */
  pointsRead?: number;
  nodesLoaded?: number;
  nodesHeld?: number;
};

export const DEFAULT_RAIL_TRACE_MODE: RailTraceMode = {
  enabled: false,
  phase: 'picking',
  targetLayerId: null,
  returnToCloudId: null,
  cloudLayerId: null,
  click: null,
  endClick: null,
  stations: [],
  startIndex: 0,
  review: DEFAULT_REVIEW,
  message: null,
  hoverS: null,
  progress: null,
  lastTrace: null,
  extending: null,
  extendStations: null,
  extendStartIndex: 0,
};

export const railTraceModeAtom = atom<RailTraceMode>(DEFAULT_RAIL_TRACE_MODE);

/**
 * The saved alignment open in the rail tool to edit, or null. A string, so readers (the saved
 * lines' renderer) re-render only when it changes, not on every progress update of the mode.
 */
export const railTraceEditingFeatureIdAtom = atom((get) => {
  const m = get(railTraceModeAtom);
  return m.enabled ? (m.editing?.featureId ?? null) : null;
});

/**
 * The rail tool's precision (its settings menu), kept per browser; read when a trace, a
 * continuation or an extension starts. 'fast' by default: on a dense scan it is 2-4x quicker,
 * reading full detail only where a station needs it and for gauge samples (`TracePrecision`).
 */
export const railTracePrecisionAtom = atomWithStorage<TracePrecision>(
  'railTracePrecision',
  'fast',
  createJSONStorage<TracePrecision>(() => localStorage),
  { getOnInit: true },
);

/** The rail tool's track gauge (its settings menu), kept per browser: grading, and the click's direction finder. */
export const railTraceGaugeAtom = atomWithStorage<TrackGaugeSetting>(
  'railTraceGauge',
  DEFAULT_TRACK_GAUGE,
  createJSONStorage<TrackGaugeSetting>(() => localStorage),
  { getOnInit: true },
);

/** The rail tool's review chart shows the gauge panel (off by default: a check, not a survey), kept per browser. */
export const railTraceShowGaugeAtom = atomWithStorage<boolean>(
  'railTraceShowGauge',
  false,
  createJSONStorage<boolean>(() => localStorage),
  { getOnInit: true },
);

// Add Polygon Mode — N-point picking with ring closure that produces a
// Polygon (1 part) or MultiPolygon (≥2 parts). Each part is one outer ring
// stored without the closing duplicate vertex; persistence appends it.
// `snapToFirst` is true while the cursor is hovering near the first vertex
// of `currentPart` AND `currentPart.length >= 3` — clicking in that state
// auto-closes the ring (equivalent to Finalize).
export type PolygonPart = Array<[number, number, number]>;

export type AddPolygonMode = {
  enabled: boolean;
  targetLayerId: string | null;
  phase: AddGeometryPhase;
  parts: PolygonPart[];
  currentPart: PolygonPart;
  snapToFirst: boolean;
  editFeatureId: string | null;
};

export const DEFAULT_ADD_POLYGON_MODE: AddPolygonMode = {
  enabled: false,
  targetLayerId: null,
  phase: 'picking',
  parts: [],
  currentPart: [],
  snapToFirst: false,
  editFeatureId: null,
};

export const addPolygonModeAtom = atom<AddPolygonMode>(DEFAULT_ADD_POLYGON_MODE);

/** Every field of `value` the same (===) as in `def`, and no other fields. */
const shallowSame = (value: object, def: object) => {
  const a = value as Record<string, unknown>;
  const b = def as Record<string, unknown>;
  const ka = Object.keys(a);
  return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
};

/**
 * Write-only atom that resets every geometry-creation tool to its default
 * disabled state. Used by `useExclusiveCreationTool` to enforce that only
 * one of {point, surface, line, polygon, rail trace} can be active at a time.
 */
export const resetAllCreationToolsAtom = atom(null, (get, set) => {
  // Only a tool not already at its defaults is reset: a fresh object for one that is re-renders
  // every reader (each Layers row reads all four modes) for nothing.
  const reset = <T extends object>(a: import('jotai').PrimitiveAtom<T>, def: T) => {
    if (!shallowSame(get(a), def)) set(a, { ...def });
  };
  reset(addPointModeAtom, { enabled: false, targetLayerId: null, draftFeature: null } as AddPointMode);
  reset(addSurfaceModeAtom, DEFAULT_ADD_SURFACE_MODE);
  reset(addLineModeAtom, DEFAULT_ADD_LINE_MODE);
  reset(addPolygonModeAtom, DEFAULT_ADD_POLYGON_MODE);
  reset(railTraceModeAtom, DEFAULT_RAIL_TRACE_MODE);
});

// Typed event emitter
class EventEmitter<T extends Record<string, unknown>> {
  private listeners = new Map<keyof T, Set<(payload: any) => void>>();

  on<K extends keyof T>(event: K, fn: (payload: T[K]) => void) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event)!.add(fn);
    return () => this.off(event, fn);
  }

  off<K extends keyof T>(event: K, fn: (payload: T[K]) => void) {
    this.listeners.get(event)?.delete(fn);
  }

  emit<K extends keyof T>(event: K, payload: T[K]) {
    this.listeners.get(event)?.forEach((fn) => fn(payload));
  }
}

type ViewerEvents = {
  jumpToTarget: { x: number; y: number; z: number; radius?: number };
  /**
   * Re-centre the orbit on a point. `along` marks a station jump (the walk
   * toolbar's follow) and carries the up-station direction: with the image
   * layers on, Viewer opens the image nearest the point facing that way
   * instead (`imageJumpView`).
   */
  setCenterTarget: { x: number; y: number; z: number; along?: [number, number, number] };
  applyCameraState: SavedViewCamera;
  /** A saved view / finding viewpoint is being applied (camera or not). */
  viewApplied: null;
  /**
   * Point the camera at a specific pose. Unlike `applyCameraState` this
   * carries no ortho/zoom/third-person state — it is a "look at this from
   * here" request, so it applies immediately in whatever projection the user
   * is in (spec §11.3). `spanM` (optional): an ortho view centres on the
   * target and zooms so that much fits on screen, looking along the pose (or
   * keeping its own direction with `keepOrthoDirection`; `lookAtCameraCommand`).
   */
  lookAt: {
    position: [number, number, number];
    target: [number, number, number];
    spanM?: number;
    keepOrthoDirection?: boolean;
  };
};

export const viewerEvents = new EventEmitter<ViewerEvents>();

// Clip Box
export type ClipBoxMode = 'NONE' | 'CLIP_OUTSIDE' | 'CLIP_INSIDE' | 'HIGHLIGHT_INSIDE';
export type ClipBoxInteractionMode = 'EDIT' | 'VIEW';

export type ClipBoxState = {
  center: [number, number, number] | null;
  halfSize: [number, number, number];
  rotation: [number, number, number, number]; // quaternion xyzw
  clipMode: ClipBoxMode;
  interactionMode: ClipBoxInteractionMode;
  enabled: boolean;
};

const DEFAULT_CLIP_BOX_STATE: ClipBoxState = {
  center: null,
  halfSize: [1, 1, 1],
  rotation: [0, 0, 0, 1],
  clipMode: 'HIGHLIGHT_INSIDE',
  interactionMode: 'EDIT',
  enabled: false,
};

const clipBoxStorage = createJSONStorage<ClipBoxState>(() => sessionStorage);

export const clipBoxStateAtom = atomFamily((projectId: string) =>
  atomWithStorage<ClipBoxState>(`@actegon.gis.clipbox.${projectId}`, DEFAULT_CLIP_BOX_STATE, clipBoxStorage),
);

// Viewer layout
export type ViewerLayout = 'full' | 'horizontal' | 'vertical';

export const viewerLayoutAtom = atomWithStorage<ViewerLayout>(
  '@actegon.gis.viewerLayout',
  'full',
  createJSONStorage<ViewerLayout>(() => sessionStorage),
);

// Transient hover state — no persistence
export const hoverImageIdAtom = atomFamily((_projectId: string) => atom<string | null>(null));

// Camera state published by 3D viewer for MapView consumption — no persistence
export type CameraMapState = {
  position: [number, number, number]; // raw CRS coords from camera
  rotationDeg: number;
  fovBase: number;
};

export const cameraMapStateAtom = atomFamily((_projectId: string) =>
  atom<CameraMapState>({ position: [0, 0, 0], rotationDeg: 0, fovBase: 60 }),
);

// Meters-per-pixel for the orthographic camera; null when perspective is active.
// Drives the OrthoScaleBar overlay. Computed from the visible viewport (zoom +
// frustum height + canvas height) — see CLAUDE.md "Screen-space sized handles".
export const orthoMetersPerPixelAtom = atomFamily((_projectId: string) => atom<number | null>(null));

/** Height of the visible ortho view in metres; null in perspective. The fly-speed chip shows the ortho pan speed from it. */
export const orthoViewHeightAtom = atomFamily((_projectId: string) => atom<number | null>(null));

/** Loaded PointCloudOctree instances for measuring tools to pick against. */
export const pointCloudTargetsAtom = atom<PointCloudOctree[]>([]);

/** The flying speed picked for a project (persisted per browser), or null to follow the scene size. */
const flySpeedOverrideAtom = atomFamily((projectId: string) => atom<number | null>(readStoredFlySpeed(projectId)));

/** Base flying speed in m/s before the Shift / Alt modifiers. Write a number to pin it, null to go back to auto. */
export const flySpeedAtom = atomFamily((projectId: string) =>
  atom(
    (get) => {
      const override = get(flySpeedOverrideAtom(projectId));
      const autoSpeed = autoFlySpeed(pointCloudsDiagonal(get(pointCloudTargetsAtom)));
      return {
        speed: override ?? autoSpeed,
        auto: override == null,
        /** The scene-sized speed; ortho panning scales by `speed / autoSpeed`. */
        autoSpeed,
      };
    },
    (_get, set, next: number | null) => {
      const v = next == null ? null : clampFlySpeed(next);
      set(flySpeedOverrideAtom(projectId), v);
      writeStoredFlySpeed(projectId, v);
    },
  ),
);

/** THREE.Object3D groups from IFC / LandXML that measuring tools should raycast against. */
export const meshPickTargetsAtom = atom<THREE.Object3D[]>([]);

/** Async pick function registered by IFC2 for fragment model raycasting.
 *  Takes (camera, mousePixel, domElement) and returns a world-space point or null. */
export type FragmentPickFn = (
  camera: THREE.Camera,
  mouse: THREE.Vector2,
  dom: HTMLElement,
) => Promise<THREE.Vector3 | null>;

export const fragmentPickFnAtom = atom<FragmentPickFn | null>(null);

/** Renders one frame of the viewer with `hide` hidden and returns it as a
 *  finding snapshot (JPEG, `encodeSnapshot`). Registered by `Viewer`'s Canvas. */
export type SnapshotFn = (hide: THREE.Object3D[]) => Promise<Blob>;
export const snapshotFnAtom = atom<SnapshotFn | null>(null);

/** An IFC element hit: world point, its layer, fragments localId and IFC GUID
 *  (null when the item carries none). */
export type FragmentElementPick = { point: THREE.Vector3; layerId: string; localId: number; guid: string | null };
/** Registered by IFC2 beside `fragmentPickFnAtom` — the same raycast, but
 *  returning the element instead of only the point. */
export type FragmentElementPickFn = (
  camera: THREE.Camera,
  mouse: THREE.Vector2,
  dom: HTMLElement,
) => Promise<FragmentElementPick | null>;
export const fragmentElementPickFnAtom = atom<FragmentElementPickFn | null>(null);

/** Live camera optics (see `cameraOptics` in `lib/findings/viewpoint.ts`). Registered by `Viewer`. */
export type CameraOpticsFn = () => { fovDeg: number; orthoViewHeightM: number | null; frustumHeight: number };
export const cameraOpticsFnAtom = atom<CameraOpticsFn | null>(null);
